import { ThunkAction, UnknownAction } from '@reduxjs/toolkit';
import type { CreateTdo } from 'veritone-types';

import { IN_REDACTION_TAG_KEY } from '@common-modules/mediaDetails';
import {
  CREATE_TDO_FAILURE,
  CREATE_TDO_SUCCESS,
  CreateTdoSuccessPayload,
} from './actions';
import { CreateTdoServiceRequest } from './models';
import callGraphQLApi from '@helpers/callGraphQLApi';
import { fetchGraphQLApi } from '@helpers/callApi';
import { TDOId } from '@common-modules/universal/models/Brands';
import { formatTDOName } from '@utils';
import { isDocumentFile } from '@helpers/filePickerSelection';
import { PDF_WASM_URL } from '@redact-modules/documents/pdfjsAssets';
import { DEFAULT_REDACTION_STATUS_LABEL } from '@common-modules/mediaDetails/redactionStatuses';

export type Thunk<P, R = any> = (
  payload: P,
  ...args: any[]
) => ThunkAction<Promise<R | undefined>, unknown, unknown, UnknownAction>;

export const serviceCreateTDO: Thunk<CreateTdoServiceRequest> =
  (req) => async (dispatch, getState) => {
    const nowISO = new Date().toISOString();
    const fileName = req.fileName && formatTDOName(req.fileName);
    // Documents don't use the ingestion settings profile (face-detection
    // thresholds, clustering, etc. are video/image-only), so don't persist it
    // on the TDO details for document uploads.
    const isDocument = isDocumentFile(req.type, req.fileName);
    const input: Partial<CreateTdo> = {
      startDateTime: nowISO,
      stopDateTime: nowISO,
      name: fileName,
      parentFolderId: req.caseId,
      addToIndex: true,
      details: {
        ...(isDocument ? {} : { settings: req.settingsProfile }),
        govQARequestId: req.govQARequestId,
        foiaXpressRequestId: req.foiaXpressRequestId,
        casepointRequestId: req.casepointRequestId,
        nuixRequestId: req.nuixRequestId,
        exterroRequestId: req.exterroRequestId,
        tags: [
          {
            value: IN_REDACTION_TAG_KEY,
            redactionStatus: DEFAULT_REDACTION_STATUS_LABEL,
          },
        ],
        veritoneProgram: {
          programLiveImage: '',
        },
      },
    };

    if (fileName || req.type) {
      Object.assign(input.details, {
        veritoneFile: {
          fileName: fileName,
          fileType: req.type,
        },
      });
    }

    const query = `
      mutation createTDO($input: CreateTDO){
      createTDO(input: $input) {
        id
        name
        status
        details
      }
    }
    `;

    return await callGraphQLApi<CreateTdoSuccessPayload>({
      actionTypes: ['NOOP', CREATE_TDO_SUCCESS, CREATE_TDO_FAILURE],
      query,
      variables: {
        input,
        url: req.fileUrl,
        unsignedUrl: req.unsignedUrl,
        error: req.error,
      },
      bailout: undefined,
      dispatch,
      getState,
    });
  };

// ---------------------------------------------------------------------------
// Fast PDF upload (VE-23391)
//
// When the uploaded file is already a PDF we skip the PDF converter engine and
// wire up the TDO directly: create the `media` asset by URI (referencing the
// already-uploaded S3 object, like the cbsa image upload flow), set it primary,
// fill in `details.veritoneFile`, and flip the TDO status to `recorded`.
// ---------------------------------------------------------------------------

interface GraphQLConfig {
  apiRoot: string;
  graphQLEndpoint: string;
  veritoneAppId: string;
}

/**
 * Create the TDO's `media` asset directly from the uploaded PDF's URI — no
 * upload of bytes, since the file already lives in S3. Mirrors the cbsa image
 * upload's `createAssetForImage`. Returns the new asset id.
 */
export async function serviceCreatePdfMediaAsset(
  tdoId: TDOId,
  uri: string,
  contentType: string,
  token: string,
  { apiRoot, graphQLEndpoint, veritoneAppId }: GraphQLConfig
): Promise<string> {
  const query = `
    mutation createPdfMediaAsset($id: ID!, $uri: String, $contentType: String, $details: JSONData) {
      createAsset(input: {
        containerId: $id,
        uri: $uri,
        assetType: "media",
        contentType: $contentType,
        details: $details
      }) {
        id
      }
    }
  `;
  const result = await fetchGraphQLApi<{ createAsset: { id: string } }>({
    endpoint: `${apiRoot}/${graphQLEndpoint}`,
    query,
    variables: {
      id: tdoId,
      uri,
      contentType,
      details: { isOriginalMedia: true },
    },
    token,
    veritoneAppId,
  });
  if (result.errors?.length) {
    throw new Error(
      `pdf media asset creation failed: ${
        result.errors[0].message ?? 'unknown error'
      }`
    );
  }
  const assetId = result.data?.createAsset?.id;
  if (!assetId) {
    throw new Error('pdf media asset creation returned no asset id');
  }
  return assetId;
}

/**
 * Keep the file the user actually uploaded, when it is not the one we open.
 *
 * TWO INDEPENDENT REASONS this helper exists:
 *
 *   - DOCUMENTS. A non-PDF upload is handed to the PDF converter, whose output
 *     becomes the TDO's `media` asset. Nothing then references the .docx /
 *     .eml / .msg the user chose — its blob sits unreferenced, and "download
 *     the original" can only ever offer the conversion.
 *   - VIDEO, AUDIO AND IMAGES (VE-30513). The app is the writer on every
 *     storage backend. The upload saga calls this through
 *     `attachOriginalMedia`, confirms the record landed, and then omits
 *     `url` from the engine payload — so on that path the ingester has no
 *     URL of its own and does not write `original-media` at all; it reads
 *     this asset as its source instead. With the kill switch on, the saga
 *     skips this call and the ingester's own write runs as before.
 *
 * A PDF upload gets NO such asset: the fast-upload path already attaches that
 * exact file as the `media` asset and marks it `details.isOriginalMedia`, so a
 * second copy would be the same blob under a second id.
 *
 * NOT primary, and a distinct `assetType`, so nothing that resolves the
 * document to render or redact — or the video to play — can reach it. This is
 * a keepsake, not a source. For A/V the `isOriginalMedia` marker is read only
 * by `findPdfOriginalMediaAsset`, which filters by `isNotPdf` first, so a
 * `video/*` or `audio/*` `contentType` is rejected before the marker is
 * consulted.
 *
 * The URI must be the PERMANENT one for the same reason the media asset's must
 * (see `serviceCreatePdfMediaAsset`): a signed URL dies in 12 hours and the
 * reference rots silently.
 */
export async function serviceCreateOriginalMediaAsset(
  tdoId: TDOId,
  uri: string,
  contentType: string,
  fileName: string,
  token: string,
  { apiRoot, graphQLEndpoint, veritoneAppId }: GraphQLConfig
): Promise<string> {
  const query = `
    mutation createOriginalMediaAsset($id: ID!, $uri: String, $contentType: String, $name: String, $details: JSONData) {
      createAsset(input: {
        containerId: $id,
        uri: $uri,
        assetType: "original-media",
        contentType: $contentType,
        name: $name,
        details: $details
      }) {
        id
      }
    }
  `;
  const result = await fetchGraphQLApi<{ createAsset: { id: string } }>({
    endpoint: `${apiRoot}/${graphQLEndpoint}`,
    query,
    variables: {
      id: tdoId,
      uri,
      contentType,
      name: fileName,
      // The name carries the extension the download needs; the flag says why
      // this asset exists at all, for anything reading details rather than type.
      details: { isOriginalMedia: true, originalFileName: fileName },
    },
    token,
    veritoneAppId,
  });
  if (result.errors?.length) {
    throw new Error(
      `original media asset creation failed: ${
        result.errors[0].message ?? 'unknown error'
      }`
    );
  }
  const assetId = result.data?.createAsset?.id;
  if (!assetId) {
    throw new Error('original media asset creation returned no asset id');
  }
  return assetId;
}

/**
 * Check whether a TDO has an `original-media` asset yet.
 *
 * Needed by the upload saga's attach-confirmation loop (VE-30513): the race
 * against a 10 s timeout cancels the saga branch but NOT the in-flight fetch,
 * so a timed-out `serviceCreateOriginalMediaAsset` call may still land on the
 * server. `createAsset` is not idempotent, so retrying blindly would create a
 * second record. Reading back disambiguates: if the TDO now carries one,
 * treat the attach as confirmed; otherwise retry.
 *
 * PRESENCE, NOT URI MATCH, and that is deliberate. The caller runs before the
 * engine request is dispatched, on a TDO `createTDO` has only just returned,
 * so the ingester cannot have written anything and no other path can have
 * touched it: any `original-media` here is necessarily the one we just wrote.
 * Comparing `uri` would add nothing and could only produce a false MISS — if
 * the platform stored the URI in any normalized form, the retry would create
 * the duplicate this read-back exists to prevent, and a second false miss
 * would fail an upload whose asset is sitting right there.
 *
 * Query shape mirrors `originalFormatAssets` in `casemedia/services/queries.ts`
 * — newest first, limit 1, since presence is the whole question.
 *
 * Returns the asset id on hit, null on miss. Any transport-level failure is
 * surfaced; the caller treats it as a miss and lets the retry take over.
 */
export async function serviceHasOriginalMediaAsset(
  tdoId: TDOId,
  token: string,
  { apiRoot, graphQLEndpoint, veritoneAppId }: GraphQLConfig
): Promise<string | null> {
  const query = `
    query hasOriginalMediaAsset($id: ID!) {
      temporalDataObject(id: $id) {
        assets(assetType: "original-media", orderBy: createdDateTime, orderDirection: desc, limit: 1) {
          records { id }
        }
      }
    }
  `;
  const result = await fetchGraphQLApi<{
    temporalDataObject: {
      assets: { records: Array<{ id: string }> };
    };
  }>({
    endpoint: `${apiRoot}/${graphQLEndpoint}`,
    query,
    variables: { id: tdoId },
    token,
    veritoneAppId,
  });
  if (result.errors?.length) {
    throw new Error(
      `original media asset lookup failed: ${
        result.errors[0].message ?? 'unknown error'
      }`
    );
  }
  const records = result.data?.temporalDataObject?.assets?.records ?? [];
  return records[0]?.id ?? null;
}

/**
 * Finalize a fast-uploaded PDF TDO: set the new media asset primary, merge the
 * `veritoneFile` metadata the GLC apps read, and flip the status to `recorded`.
 * Reads current details first so unrelated fields (tags, settings, …) survive
 * the merge — same defensive read used by `serviceUpdateTdoName` in the
 * documents module.
 */
export async function serviceFinalizePdfTdo(
  tdoId: TDOId,
  assetId: string,
  meta: { fileName: string; fileType: string; pageCount: number },
  token: string,
  { apiRoot, graphQLEndpoint, veritoneAppId }: GraphQLConfig
): Promise<void> {
  const readQuery = `
    query($id: ID!) {
      temporalDataObject(id: $id) {
        details
      }
    }
  `;
  const readResult = await fetchGraphQLApi<{
    temporalDataObject: { details: Record<string, unknown> | null } | null;
  }>({
    endpoint: `${apiRoot}/${graphQLEndpoint}`,
    query: readQuery,
    variables: { id: tdoId },
    token,
    veritoneAppId,
  });
  if (readResult.errors?.length) {
    throw new Error(
      `tdo details fetch failed: ${
        readResult.errors[0].message ?? 'unknown error'
      }`
    );
  }
  const details = (readResult.data?.temporalDataObject?.details ??
    {}) as Record<string, unknown> & {
    veritoneFile?: Record<string, unknown> | null;
  };

  const mutation = `
    mutation finalizePdfTdo($id: ID!, $assetId: ID!, $details: JSONData) {
      updateTDO(input: {
        id: $id,
        status: "recorded",
        primaryAsset: { id: $assetId, assetType: "media" },
        details: $details
      }) {
        id
        status
      }
    }
  `;
  const writeResult = await fetchGraphQLApi<{
    updateTDO: { id: string; status: string };
  }>({
    endpoint: `${apiRoot}/${graphQLEndpoint}`,
    query: mutation,
    variables: {
      id: tdoId,
      assetId,
      details: {
        ...details,
        veritoneFile: {
          ...(details.veritoneFile ?? {}),
          fileName: meta.fileName,
          filename: meta.fileName,
          fileType: meta.fileType,
          mimeType: meta.fileType,
          pageCount: meta.pageCount,
          readyForGLCApps: true,
        },
      },
    },
    token,
    veritoneAppId,
  });
  if (writeResult.errors?.length) {
    throw new Error(
      `tdo finalize failed: ${writeResult.errors[0].message ?? 'unknown error'}`
    );
  }
}

/**
 * Count the pages of the PDF at `url` via pdfjs. Dynamically imports react-pdf
 * so the main-page (FilePicker) bundle doesn't statically pull in pdfjs. The
 * worker path matches the one the document editor configures
 * (`pdfPageDimensions.ts`).
 */
export async function fetchPdfPageCount(url: string): Promise<number> {
  const { pdfjs } = await import('react-pdf');
  pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';
  const doc = await pdfjs.getDocument({ url, wasmUrl: PDF_WASM_URL }).promise;
  try {
    return doc.numPages;
  } finally {
    await doc.destroy();
  }
}
